// Queries de ENTREGAS DE PIENSO (Módulo 2).
//
// Decisiones:
// - staleTime 60s (los datos de pienso cambian a menudo durante el
//   ciclo de cebo).
// - Invalidamos la query de lista y de KPI al crear (la nueva entrega
//   afecta a ambas).

import { apiFetch, qk, useApiMutation, useApiQuery } from './client';
import type {
  ConsumoPienso,
  CurvaPienso,
  EntregaPienso,
  EntregaPiensoCreateInput,
} from '@/lib/types';

const ENTREGAS_KEY = qk('entregas-pienso');

/** Lista de entregas de pienso de una camada (ordenadas por fecha asc). */
export function useEntregasDeCamada(camadaId: number | null) {
  return useApiQuery<EntregaPienso[]>({
    queryKey:
      camadaId != null
        ? [...ENTREGAS_KEY, 'camada', camadaId]
        : qk('entregas-pienso', 'camada', 'none'),
    queryFn: async () => {
      const entregas = await apiFetch<Array<Omit<EntregaPienso, 'kg'> & { kg: number | string }>>(
        `/v1/camadas/${camadaId}/entregas-pienso`,
      );
      return entregas.map((entrega) => ({ ...entrega, kg: Number(entrega.kg) }));
    },
    enabled: camadaId != null,
    staleTime: 60 * 1000,
  });
}

/** KPIs de pienso de la camada (total entregado, sobrante, consumido). */
export function useConsumoPienso(camadaId: number | null) {
  return useApiQuery<ConsumoPienso>({
    queryKey:
      camadaId != null
        ? [...ENTREGAS_KEY, 'consumo', camadaId]
        : qk('entregas-pienso', 'consumo', 'none'),
    queryFn: () => apiFetch<ConsumoPienso>(`/v1/camadas/${camadaId}/consumo-pienso`),
    enabled: camadaId != null,
    staleTime: 60 * 1000,
  });
}

/** Curva esperada de pienso acumulado de una camada (histórico/Ajustes). */
export function useCurvaPienso(camadaId: number | null) {
  return useApiQuery<CurvaPienso>({
    queryKey:
      camadaId != null
        ? [...ENTREGAS_KEY, 'curva', camadaId]
        : qk('entregas-pienso', 'curva', 'none'),
    queryFn: () => apiFetch<CurvaPienso>(`/v1/camadas/${camadaId}/curva-pienso`),
    enabled: camadaId != null,
    staleTime: 60 * 1000,
  });
}

/** Crea una entrega de pienso. Invalida la lista y el KPI de la camada. */
export function useCreateEntregaPienso(camadaId: number) {
  return useApiMutation<EntregaPiensoCreateInput, EntregaPienso>({
    mutationFn: (data, context) =>
      apiFetch<EntregaPienso>(`/v1/camadas/${camadaId}/entregas-pienso`, {
        offlineBase: context.offlineBase,
        method: 'POST',
        body: JSON.stringify(data),
      }),
    invalidateKeys: [
      ['liquidacion-evolucion'],
      [...ENTREGAS_KEY, 'camada', camadaId],
      [...ENTREGAS_KEY, 'consumo', camadaId],
    ],
  });
}

/** Actualiza una entrega existente y refresca listado, KPI y gráfica real. */
export function useUpdateEntregaPienso(camadaId: number, entregaId: number) {
  return useApiMutation<EntregaPiensoCreateInput, EntregaPienso>({
    mutationFn: (data, context) =>
      apiFetch<EntregaPienso>(`/v1/camadas/${camadaId}/entregas-pienso/${entregaId}`, {
        offlineBase: context.offlineBase,
        method: 'PATCH',
        body: JSON.stringify(data),
      }),
    invalidateKeys: [
      ['liquidacion-evolucion'],
      [...ENTREGAS_KEY, 'camada', camadaId],
      [...ENTREGAS_KEY, 'consumo', camadaId],
    ],
  });
}
